import React, { useState } from 'react';
import { useT } from '../lib/i18n';
import { useSelector } from 'react-redux';
import { RootState, Session } from '../store';

export function CalendarView() {
  const sessions = useSelector((s: RootState) => s.sessions.list);
  const [offset, setOffset] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);

  const { t, lang } = useT();
  const base = new Date();
  base.setDate(1);
  base.setMonth(base.getMonth() + offset);
  const year = base.getFullYear();
  const month = base.getMonth();
  const monthStr = base.toLocaleString(lang === 'en' ? 'en-US' : 'es-AR', { month: 'long', year: 'numeric' });

  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const prefix = `${year}-${String(month + 1).padStart(2, '0')}`;

  const dayMap: Record<number, { profit: number; sessions: Session[] }> = {};
  for (const s of sessions) {
    if (!s.date.startsWith(prefix)) continue;
    const d = parseInt(s.date.split('-')[2]);
    if (!dayMap[d]) dayMap[d] = { profit: 0, sessions: [] };
    dayMap[d].profit += s.netProfit;
    dayMap[d].sessions.push(s);
  }

  const cells: (number | null)[] = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) cells.push(d);

  const monthProfit = Object.values(dayMap).reduce((acc, v) => acc + v.profit, 0);
  const monthSessions = Object.values(dayMap).reduce((acc, v) => acc + v.sessions.length, 0);
  const winDays = Object.values(dayMap).filter(v => v.profit > 0).length;
  const lossDays = Object.values(dayMap).filter(v => v.profit < 0).length;
  const selectedSessions = selected && dayMap[selected] ? dayMap[selected].sessions : [];

  const DOW_ES = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];
  const DOW_EN = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dowLabels = lang === 'en' ? DOW_EN : DOW_ES;

  return (
    <div className="calendar-view">
      <div className="cal-top">
        <div className="cal-nav-row">
          <button className="cal-nav-big" onClick={() => { setOffset(o => o - 1); setSelected(null); }}>
            {t('calendar.prev')}
          </button>
          <h2 className="cal-month-title">{monthStr.toUpperCase()}</h2>
          <button className="cal-nav-big" onClick={() => { setOffset(o => o + 1); setSelected(null); }}>
            {t('calendar.next')}
          </button>
        </div>
        <div className="cal-month-stats">
          <div className="cms"><span>{t('calendar.profit')}</span><span className={monthProfit >= 0 ? 'pos' : 'neg'}>{monthProfit >= 0 ? '+' : ''}${monthProfit.toFixed(2)}</span></div>
          <div className="cms"><span>{t('calendar.sessions')}</span><span>{monthSessions}</span></div>
          <div className="cms"><span>{t('calendar.winDays')}</span><span className="pos">{winDays}</span></div>
          <div className="cms"><span>{t('calendar.lossDays')}</span><span className="neg">{lossDays}</span></div>
        </div>
      </div>

      <div className="big-cal">
        <div className="big-cal-dow">
          {dowLabels.map(d => <div key={d}>{d}</div>)}
        </div>
        <div className="big-cal-grid">
          {cells.map((d, i) => {
            if (d === null) return <div key={`e${i}`} className="big-cal-cell empty" />;
            const data = dayMap[d];
            const cls = !data ? '' : data.profit > 0 ? 'win' : data.profit < 0 ? 'loss' : 'breakeven';
            return (
              <div
                key={d}
                className={`big-cal-cell ${cls} ${selected === d ? 'selected' : ''}`}
                onClick={() => setSelected(selected === d ? null : d)}
              >
                <span className="cell-day">{d}</span>
                {data && (
                  <>
                    <span className={`cell-profit ${data.profit >= 0 ? 'pos' : 'neg'}`}>
                      {data.profit >= 0 ? '+' : ''}${data.profit.toFixed(0)}
                    </span>
                    <span className="cell-count">{data.sessions.length}s</span>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {selected && selectedSessions.length > 0 && (
        <div className="cal-detail">
          <div className="panel-title">{prefix}-{String(selected).padStart(2, '0')}</div>
          {selectedSessions.map(s => (
            <div key={s.id} className={`cal-session-row ${s.netProfit >= 0 ? 'win' : 'loss'}`}>
              <span>{s.platform}</span>
              <span>
                {s.type === 'cash'
                  ? `Cash ${s.stakes}`
                  : s.type === 'multi'
                    ? t('calendar.tournaments', { n: s.tournaments.length })
                    : s.tournaments[0]?.format}
              </span>
              <span>${s.totalInvested.toFixed(2)} inv.</span>
              <span className={s.netProfit >= 0 ? 'pos' : 'neg'}>
                {s.netProfit >= 0 ? '+' : ''}${s.netProfit.toFixed(2)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}